/**
 * @fileoverview Document upload to CAVE for processing.
 *
 * Handles PDF file validation, base64 encoding, and intake API calls.
 * Returns a contract object (id, bucket, pdfKey) that downstream operations
 * use to poll status and fetch artifacts.
 */

import { apiRequest } from 'platform/utilities/api';
import { buildIntakeUrl } from './endpoints';
import { createCaveError } from './errors';

/**
 * Checks if a file is a PDF based on MIME type or file extension.
 * @param {File} file - The file to check
 * @returns {boolean} True if file is a PDF
 */
export const isPdfFile = file =>
  file?.type === 'application/pdf' ||
  file?.name?.toLowerCase().endsWith('.pdf');

/**
 * Reads a File object and encodes it as base64.
 * @async
 * @param {File} file - The file to read
 * @returns {Promise<string>} The base64-encoded file content (without data: prefix)
 * @throws {Error} If file reading fails
 */
const readFileAsBase64 = file =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onerror = () => {
      reject(new Error('Unable to prepare PDF for CAVE upload.'));
    };

    reader.onload = () => {
      if (typeof reader.result !== 'string') {
        reject(new Error('Unable to prepare PDF for CAVE upload.'));
        return;
      }

      const [, pdfB64] = reader.result.split(',');
      if (!pdfB64) {
        reject(new Error('Unable to prepare PDF for CAVE upload.'));
        return;
      }

      resolve(pdfB64);
    };

    reader.readAsDataURL(file);
  });

/**
 * Uploads a PDF document to CAVE for automated processing.
 *
 * Validates file type, reads as base64, sends to intake API, and validates
 * the response contract (id, bucket, pdfKey) before returning.
 *
 * @async
 * @param {File} file - The PDF file to upload
 * @returns {Promise<{id: string, bucket: string, pdfKey: string}>} The contract
 *   object for downstream status polling and artifact fetching
 * @throws {Error} If file is not a PDF, reading fails, API fails, or response
 *   is missing required fields (id, bucket, pdfKey)
 */
export const uploadDocument = async file => {
  if (!isPdfFile(file)) {
    throw new Error('Unsupported file type for CAVE upload.');
  }

  const pdfB64 = await readFileAsBase64(file);
  const requestBody = Object.fromEntries([
    ['pdf_b64', pdfB64],
    ['file_name', file.name],
  ]);

  let payload;
  try {
    payload = await apiRequest(buildIntakeUrl(), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestBody),
    });
  } catch (error) {
    throw createCaveError(error, {
      prefix: 'CAVE intake failed',
      fallbackDetail: 'CAVE intake request failed.',
    });
  }

  const { id, bucket, pdfKey } = payload || {};

  if (!id) {
    throw new Error(
      'CAVE intake succeeded but no document id was returned.' +
        ' The document could not be queued for automated review.',
    );
  }

  // Validate the remaining fields the downstream workflow depends on. A
  // response missing any of these is not a usable intake result — fail clearly
  // instead of proceeding with an incomplete contract that would fail later,
  // opaquely.
  const missingFields = [
    ['bucket', bucket],
    ['pdfKey', pdfKey],
  ]
    .filter(([, value]) => value == null || value === '')
    .map(([field]) => field);

  if (missingFields.length > 0) {
    throw new Error(
      `CAVE intake response was incomplete (missing: ${missingFields.join(
        ', ',
      )}). The document could not be queued for automated review.`,
    );
  }

  return { id, bucket, pdfKey };
};

export default uploadDocument;
